import { z } from 'zod';
import { UserRole } from '../enums';
import { ErrorCode } from '../errors';

/**
 * Password policy, mirrored on client and server.
 * Kept intentionally strict: min 10 chars, at least one letter and one digit.
 * Server additionally blocks a top-N common-password list (see api/auth/password.service.ts).
 */
export const passwordPolicy = z
  .string()
  .min(10, 'Password must be at least 10 characters.')
  .max(128, 'Password must be at most 128 characters.')
  .regex(/[A-Za-z]/, 'Password must contain at least one letter.')
  .regex(/[0-9]/, 'Password must contain at least one digit.');

export const loginRequest = z.object({
  email: z.string().email().toLowerCase().trim(),
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof loginRequest>;

export const changePasswordRequest = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: passwordPolicy,
  })
  .refine((v) => v.currentPassword !== v.newPassword, {
    path: ['newPassword'],
    message: 'New password must differ from the current one.',
  });
export type ChangePasswordRequest = z.infer<typeof changePasswordRequest>;

export const refreshRequest = z.object({}).optional();

export const authUser = z.object({
  id: z.string().cuid(),
  email: z.string().email(),
  fullName: z.string(),
  role: z.nativeEnum(UserRole),
  mustChangePassword: z.boolean(),
  permissions: z.array(z.string()),
});
export type AuthUser = z.infer<typeof authUser>;

export const loginResponse = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: z.string().datetime(),
  user: authUser,
});
export type LoginResponse = z.infer<typeof loginResponse>;

export const refreshResponse = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: z.string().datetime(),
});
export type RefreshResponse = z.infer<typeof refreshResponse>;

export const errorEnvelope = z.object({
  code: z.nativeEnum(ErrorCode),
  message: z.string(),
  details: z.unknown().optional(),
  requestId: z.string().uuid(),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelope>;
